import React from 'react';

import cs from 'classnames';
import { createPortal } from 'react-dom';
import { ToggleLayer } from 'react-laag';

import ItemLink from 'components/Layouts/Admin/Dashboard/Navigation/ItemLink';
import Button from 'components/shared/Button';
import Divider from 'components/shared/Divider';
import Dropdown from 'components/shared/Dropdown';
import ArrowDown from 'components/shared/Icon/icons/ArrowDown';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { isDownloadingInProgress } from 'utils/filesDownloaders/downloadingTaskManager';

import SaveAsWordDoc from '../../ItinerariesItemBuilder/ActionButtons/SaveAsWordDoc';
import s from './SaveMenu.module.css';

type Props = {
  buttonClassName: string;
  type: 'primary' | 'grey';
  disabled: boolean;
  hasSaveAsSampleButton: boolean;
  itinerariesItemId: string;
  canCreateItineraryLayouts: boolean;
  saveCallback: () => Promise<void>;
  pdfCallback?: () => Promise<void>;
  docxCallback?: () => Promise<void>;
  saveAsSampleCallback?: () => Promise<void>;
};

type DropDownProps = Props & {
  onClose: () => void;
  children?: React.ReactNode;
};

export const SaveMenu = (props: Props) => {
  const { isMobile } = useMediaBreakpoints();

  if (isMobile) {
    return <MobileSaveMenu {...props} />;
  }

  return <DesktopSaveMenu {...props} />;
};

const DesktopSaveMenu = (props: Props) => {
  return (
    <ToggleLayer
      renderLayer={({ isOpen, layerProps, close }) =>
        isOpen &&
        createPortal(
          <div
            ref={layerProps.ref}
            className="layer"
            style={{
              ...layerProps.style,

              zIndex: 9999,
            }}
          >
            <SaveMenuDropdown {...props} onClose={close} />
          </div>,
          document.body,
        )
      }
      fixed
      placement={{
        anchor: 'BOTTOM_RIGHT',
        autoAdjust: true,
        triggerOffset: 10,
      }}
      closeOnOutsideClick
    >
      {({ triggerRef, toggle }) => {
        return (
          <Button.Group shape="ellipse" type={props.type} disabled={props.disabled}>
            {/* Save */}
            <Button
              type={props.type}
              disabled={props.disabled}
              className={s.saveButton}
              onClick={() => {
                props.saveCallback();
              }}
            >
              Save
            </Button>

            {/* Dropdown */}
            <Button
              anchorRef={triggerRef}
              type={props.type}
              size="small"
              className={s.dropdownButton}
              onClick={toggle}
              disabled={props.disabled}
            >
              <ArrowDown
                className={cs(
                  s.dropdownButtonIcon,
                  props.disabled && s.dropdownButtonIconDisabled,
                  props.type === 'grey' && s.dropdownButtonIconTypeGrey,
                  props.type === 'primary' && s.dropdownButtonIconTypePrimary,
                )}
              />
            </Button>
          </Button.Group>
        );
      }}
    </ToggleLayer>
  );
};

const MobileSaveMenu = (props: Props) => {
  return (
    <ToggleLayer
      renderLayer={({ isOpen, layerProps, close }) =>
        isOpen &&
        createPortal(
          <div
            ref={layerProps.ref}
            className="layer"
            style={{
              ...layerProps.style,
              zIndex: 9999,
            }}
          >
            <SaveMenuDropdown {...props} onClose={close}>
              <ItemLink
                isButton
                disabled={props.disabled}
                className={s.linkItem}
                linkClassName={s.dropdownButton}
                onClick={() => {
                  close();
                  props.saveCallback();
                }}
                noUnderline
                noMargin
              >
                Save
              </ItemLink>
            </SaveMenuDropdown>
          </div>,
          document.body,
        )
      }
      fixed
      placement={{
        anchor: 'BOTTOM_RIGHT',
        autoAdjust: true,
        triggerOffset: 10,
      }}
      closeOnOutsideClick
    >
      {({ triggerRef, toggle }) => {
        return (
          <Button
            anchorRef={triggerRef}
            shape="ellipse"
            type={props.type}
            size="medium"
            className={props.buttonClassName}
            onClick={toggle}
            disabled={props.disabled}
          >
            Save
          </Button>
        );
      }}
    </ToggleLayer>
  );
};

const SaveMenuDropdown = (props: DropDownProps) => {
  return (
    <Dropdown className={s.dropdown}>
      {props.children}

      {props.pdfCallback && (
        <ItemLink
          isButton
          className={s.linkItem}
          linkClassName={s.dropdownButton}
          onClick={() => {
            props.onClose();
            props.pdfCallback!();
          }}
          disabled={isDownloadingInProgress(props.itinerariesItemId)}
          noUnderline
          noMargin
        >
          Save as PDF
        </ItemLink>
      )}
      {props.docxCallback && (
        <ItemLink
          isButton
          className={s.linkItem}
          linkClassName={s.dropdownButton}
          onClick={() => {
            props.onClose();
            props.docxCallback!();
          }}
          disabled={isDownloadingInProgress(props.itinerariesItemId)}
          noUnderline
          noMargin
        >
          <SaveAsWordDoc />
        </ItemLink>
      )}
      {props.hasSaveAsSampleButton && (
        <>
          <Divider className={s.saveMenuDivider} />
          <ItemLink
            isButton
            className={s.linkItem}
            linkClassName={s.dropdownButton}
            onClick={() => {
              props.onClose();
              props.saveAsSampleCallback!();
            }}
            noUnderline
            noMargin
            disabled={!props.canCreateItineraryLayouts}
          >
            Save as Template
          </ItemLink>
        </>
      )}
    </Dropdown>
  );
};
